/**
 * GLOBAL-SHELL-IMPLEMENTATION-000001 — Founder primary nav (≤15).
 * Source: docs/platform/shell/SIDEBAR-STANDARD.md
 */
import type { NavItem } from '../api/alpha';

export type ShellNavMode = 'founder' | 'engineer';

/** Canonical founder destinations — order is authoritative. */
export const FOUNDER_NAV_ORDER: readonly { id: string; label: string; path: string; group: string }[] = [
  { id: 'nav-dashboard', label: 'Dashboard', path: '/dashboard', group: 'Home' },
  { id: 'nav-command', label: 'Command', path: '/command', group: 'Home' },
  { id: 'nav-products', label: 'Products', path: '/amazon/catalog', group: 'Commerce' },
  { id: 'nav-inventory-ms', label: 'Inventory', path: '/amazon/inventory', group: 'Commerce' },
  { id: 'nav-orders-ms', label: 'Orders', path: '/amazon/orders', group: 'Commerce' },
  { id: 'nav-order-items', label: 'Order Items', path: '/amazon/order-items', group: 'Commerce' },
  { id: 'nav-spapi-v2-orders', label: 'Sync V2', path: '/amazon/spapi-v2/orders', group: 'Control' },
  { id: 'nav-amazon-pricing', label: 'Pricing', path: '/amazon/pricing', group: 'Commerce' },
  { id: 'nav-amazon-finance', label: 'Finance', path: '/amazon/finance', group: 'Commerce' },
  { id: 'nav-amazon-connect', label: 'Connect', path: '/amazon/connect', group: 'Control' },
  { id: 'nav-system-explorer', label: 'System Explorer', path: '/amazon/system-explorer', group: 'Control' },
  { id: 'nav-workspaces', label: 'Workspaces', path: '/workspaces', group: 'Platform' },
  { id: 'nav-master-data', label: 'Master Data', path: '/settings/master-data', group: 'Platform' },
  { id: 'nav-account', label: 'Account', path: '/account/profile', group: 'Account' },
] as const;

export const FOUNDER_PATHS = new Set(FOUNDER_NAV_ORDER.map((n) => n.path));

export function buildFounderNav(): NavItem[] {
  return FOUNDER_NAV_ORDER.map((n, i) => ({
    id: n.id,
    label: n.label,
    path: n.path,
    module: n.path.startsWith('/amazon') ? 'amazonconnect' : 'shell',
    group: n.group,
    order: (i + 1) * 10,
  }));
}

/** Map full nav list into founder primary (≤15) using blueprint order. */
export function toFounderNav(all: NavItem[]): NavItem[] {
  const byPath = new Map(all.map((n) => [n.path, n]));
  return FOUNDER_NAV_ORDER.map((spec, i) => {
    const hit = byPath.get(spec.path);
    return {
      id: hit?.id ?? spec.id,
      label: spec.label,
      path: spec.path,
      module: hit?.module ?? 'shell',
      group: spec.group,
      order: (i + 1) * 10,
      icon: hit?.icon,
    };
  });
}

/** Engineer mode: full list minus exact founder duplicates preferred; keep full API list. */
export function resolveShellNav(all: NavItem[], mode: ShellNavMode): NavItem[] {
  if (mode === 'founder') return toFounderNav(all.length ? all : buildFounderNav());
  const base = all.length ? all : buildFounderNav();
  // Ensure founder destinations remain present even if API drops them.
  const paths = new Set(base.map((n) => n.path));
  const extras = toFounderNav(base).filter((n) => !paths.has(n.path));
  return [...base, ...extras].sort((a, b) => a.order - b.order || a.label.localeCompare(b.label));
}

export function rankNavForCommand(items: NavItem[], query: string): NavItem[] {
  const q = query.trim().toLowerCase();
  const scored = items.map((item) => {
    let score = 0;
    if (FOUNDER_PATHS.has(item.path)) score += 100;
    if (!q) return { item, score: score - item.order };
    const label = item.label.toLowerCase();
    const path = item.path.toLowerCase();
    const group = item.group.toLowerCase();
    if (label.startsWith(q)) score += 50;
    else if (label.includes(q)) score += 30;
    if (path.includes(q)) score += 20;
    if (group.includes(q)) score += 10;
    if (score < 20 && !label.includes(q) && !path.includes(q) && !group.includes(q)) {
      return { item, score: -1 };
    }
    return { item, score: score - item.order * 0.01 };
  });
  return scored
    .filter((s) => s.score >= 0)
    .sort((a, b) => b.score - a.score)
    .map((s) => s.item)
    .slice(0, 12);
}
